const FRACTION_DELTA = 0.02;
const SUPPORTED_FRACTIONS = [
    { value: 1 / 8, label: '1/8' },
    { value: 1 / 4, label: '1/4' },
    { value: 1 / 3, label: '1/3' },
    { value: 3 / 8, label: '3/8' },
    { value: 1 / 2, label: '1/2' },
    { value: 5 / 8, label: '5/8' },
    { value: 2 / 3, label: '2/3' },
    { value: 3 / 4, label: '3/4' },
    { value: 7 / 8, label: '7/8' },
];

function formatFractionQuantity(quantity: number): string | null {
    if (!Number.isFinite(quantity) || quantity < 0) {
        return null;
    }

    const whole = Math.floor(quantity);
    const remainder = quantity - whole;

    if (remainder <= FRACTION_DELTA) {
        return whole > 0 ? `${whole}` : null;
    }

    if (1 - remainder <= FRACTION_DELTA) {
        return `${whole + 1}`;
    }

    let closestFraction: { value: number; label: string } | null = null;
    let closestDelta = Number.POSITIVE_INFINITY;

    for (const fraction of SUPPORTED_FRACTIONS) {
        const delta = Math.abs(remainder - fraction.value);
        if (delta < closestDelta) {
            closestDelta = delta;
            closestFraction = fraction;
        }
    }

    if (!closestFraction || closestDelta > FRACTION_DELTA) {
        return null;
    }

    return whole > 0 ? `${whole} ${closestFraction.label}` : closestFraction.label;
}

function escapeRegExp(value: string): string {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function wordMatchPattern(word: string): RegExp {
    return new RegExp(`\\b${escapeRegExp(word.trim())}\\b`, 'i');
}

// True when `word` already appears as a whole word inside `ingredientName`,
// case-insensitively (e.g. "fillet" matches "Trout Fillet"; "pea" does not
// match inside "Peach" thanks to \b word-boundary matching).
function ingredientNameContainsWord(ingredientName: string, word: string): boolean {
    const trimmed = word.trim();
    if (trimmed === '') {
        return false;
    }
    return wordMatchPattern(trimmed).test(ingredientName);
}

// True when the unit name (singular and/or plural, comma-separated, as
// stored in unitExtra/unitName) is redundant with the ingredient name.
// Checks BOTH forms regardless of which the current quantity would select,
// because a name can be singular ("Trout Fillet") while quantity=2 selects
// the plural display form ("fillets"), which alone wouldn't word-match.
function isUnitNameRedundantWithIngredientName(ingredientName: string | undefined, unitExtra: string | undefined): boolean {
    if (!ingredientName || !unitExtra) {
        return false;
    }
    const forms = unitExtra.split(',').map((form) => form.trim()).filter((form) => form !== '');
    return forms.some((form) => ingredientNameContainsWord(ingredientName, form));
}

// Preserves the matched text's case pattern on the replacement: ALL CAPS
// stays all caps, Title Case (first letter capitalized) stays title case,
// otherwise lowercase (unitExtra forms are authored lowercase).
function matchCase(source: string, replacement: string): string {
    if (source === source.toUpperCase() && source !== source.toLowerCase()) {
        return replacement.toUpperCase();
    }
    if (source[0] === source[0].toUpperCase() && source[0] !== source[0].toLowerCase()) {
        return replacement.charAt(0).toUpperCase() + replacement.slice(1);
    }
    return replacement;
}

// When unit is 'None' and unitExtra's singular/plural word is already part
// of `name`, returns `name` with that word replaced by the correct form for
// `quantity` (e.g. "Trout Fillet" + "fillet,fillets" + qty=2 -> "Trout
// Fillets"). Otherwise returns `name` unchanged.
export function FormatIngredientDisplayName(name: string, unit: string, unitExtra: string | undefined, quantity: number): string {
    if (unit !== 'None' || !unitExtra) {
        return name;
    }
    const forms = unitExtra.split(',').map((form) => form.trim()).filter((form) => form !== '');
    if (forms.length === 0) {
        return name;
    }
    const singular = forms[0];
    const plural = forms.length > 1 ? forms[1] : forms[0];
    const target = quantity === 1 ? singular : plural;
    for (const candidate of [singular, plural]) {
        if (!candidate) {
            continue;
        }
        const pattern = wordMatchPattern(candidate);
        const match = name.match(pattern);
        if (match) {
            return name.replace(pattern, matchCase(match[0], target));
        }
    }
    return name;
}

export type FormatUnitLabelOptions = {
    longForm?: boolean;
};

export function FormatUnitLabel(quantity: number, unit: string, unitExtra?: string, ingredientName?: string, options?: FormatUnitLabelOptions): string {
    let unitShort = unit;
    const unitExtraTitle = unitExtra ? unitExtra.split(',') : [];
    const longForm = options?.longForm ?? false;
    switch (unit) {
        case 'Cup':
            unitShort = (quantity === 1) ? 'cup' : 'cups';
            break;
        case 'Tablespoon':
            unitShort = longForm ? ((quantity === 1) ? 'tablespoon' : 'tablespoons') : 'tbsp';
            break;
        case 'Teaspoon':
            unitShort = longForm ? ((quantity === 1) ? 'teaspoon' : 'teaspoons') : 'tsp';
            break;
        case 'Gram':
            unitShort = longForm ? ((quantity === 1) ? 'gram' : 'grams') : 'g';
            break;
        case 'Milliliter':
            unitShort = longForm ? ((quantity === 1) ? 'millilitre' : 'millilitres') : 'ml';
            break;
        case 'Pinch':
            unitShort = (quantity === 1) ? 'pinch' : 'pinches';
            break;
        case 'None':
            if (isUnitNameRedundantWithIngredientName(ingredientName, unitExtra)) {
                unitShort = '';
            } else {
                unitShort = (quantity === 1) ? (unitExtraTitle.length > 0 ? unitExtraTitle[0] : '') : (unitExtraTitle.length > 1 ? unitExtraTitle[1] : '');
            }
            break;
    }
    return unitShort;
}

export function FormatQuantityAndUnit(quantity: number, unit: string, fixed: number, unitExtra?: string, ingredientName?: string, disableFraction?: boolean): string {
    const unitShort = FormatUnitLabel(quantity, unit, unitExtra, ingredientName);
    const formattedQuantity = fixed > 0 && !disableFraction
        ? (formatFractionQuantity(quantity) ?? `${parseFloat(quantity.toFixed(fixed))}`)
        : `${parseFloat(quantity.toFixed(fixed))}`;
    return unitShort ? `${formattedQuantity} ${unitShort}` : formattedQuantity;
}
